import { useTranslation } from "react-i18next"

import { LeaderboardTrend } from "@/components/leaderboard/leaderboard-trend"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Card, CardContent } from "@/components/ui/card"
import { useInitials } from "@/hooks/use-initials"
import { formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import type { LeaderboardPeriod, LeaderboardRow } from "@/types/leaderboard"

/**
 * OKS-51 — Sticky "Your rank" card above the rankings list. Renders
 * ONLY on the creator surface, ONLY when the viewer is outside the
 * visible top-50 (UX Spec §1, §2.1).
 *
 * Composition:
 *   - Avatar (fallback initials when `avatar_url` is null — V1)
 *   - "Your rank: #N of {M}" (bold, primary fg)
 *   - "{xp} XP this {window_label}" (muted)
 *
 * Accent treatment matches the in-list viewer-row highlight so the
 * visual concept reads consistently across both branches.
 *
 * # Accessibility
 *
 *   - Wrapper is a `<section>` with `aria-label="Your rank"` so SRs
 *     announce its purpose distinctly from the rankings list (UX
 *     Spec §6).
 *   - Non-animated — server-rendered alongside the rankings on each
 *     filter change. No transition affordances in V1.
 */
interface LeaderboardSelfPinProps {
  selfRank: LeaderboardRow
  period: LeaderboardPeriod
  brandMemberCount: number
  className?: string
}

export function LeaderboardSelfPin({
  selfRank,
  period,
  brandMemberCount,
  className,
}: LeaderboardSelfPinProps) {
  const { t, i18n } = useTranslation()
  const initials = useInitials()
  const formattedXp = formatXpNumber(selfRank.xp, i18n.language)
  const windowLabel = t(`leaderboard.window.${period}`)

  return (
    <section
      aria-label={t("leaderboard.self_pin.aria_label")}
      data-testid="leaderboard-self-pin"
      className={cn("sticky top-0 z-10", className)}
    >
      <Card className="border-accent bg-accent/10 border shadow-sm">
        <CardContent className="flex items-center gap-3 px-4 py-3">
          <Avatar className="size-10 shrink-0">
            <AvatarFallback className="text-sm font-semibold">
              {initials(selfRank.display_name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-foreground text-sm font-semibold">
              {t("leaderboard.self_pin.label", {
                rank: selfRank.rank,
                total: brandMemberCount,
              })}
            </p>
            <p className="text-muted-foreground text-xs tabular-nums">
              {t("leaderboard.self_pin.xp", {
                xp: formattedXp,
                window: windowLabel,
              })}
            </p>
          </div>
          {/* OKS-394 — same trend indicator as the in-list rows. */}
          <LeaderboardTrend trend={selfRank.trend} />
        </CardContent>
      </Card>
    </section>
  )
}
